import React, { useState, useEffect } from "react";
import axios from "axios";
import Card from "components/card";
import {
  MdWaterDrop,
  MdTrendingDown,
  MdWarning,
  MdAnalytics,
  MdBalance,
  MdFactory,
  MdOutlineWarehouse,
  MdFilterAlt,
  MdInventory,
  MdLocalShipping,
  MdBugReport,
  MdTrendingUp,
  MdCheckCircle,
  MdExpandMore,
  MdExpandLess,
  MdRefresh,
  MdStackedBarChart,
  MdBarChart,
  MdPieChart,
  MdTableChart,
  MdAssessment,
  MdTranslate,
} from "react-icons/md";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  Label,
  LabelList,
  LineChart,
  Line,
  ReferenceLine,
  PieChart,
  Pie,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-hot-toast";

// Translations object
const translations = {
  en: {
    totalStock: "Total Stock",
    averageMoisture: "Average Receiving Moisture",
    mostPrevalentSize: "Most Prevalent Size",
    premiumQualityRegion: "Premium Quality Region",
    moistureLevels: "Average Moisture Levels Across Processing Stages",
    moistureContentAnalysis: "Moisture Content Analysis",
    moistureLossProcess: "Moisture Loss Process Flow",
    receivingStage: "Receiving Stage",
    calibrationStage: "Calibration Stage",
    consumptionStage: "Consumption Stage",
    initialMoisture: "Initial moisture",
    calibratedMoisture: "Calibrated moisture",
    finalMoisture: "Final moisture",
    lossFromAnalysis: "Loss from analysis",
    gainFromCalibration: "Gain from calibration",
    lossFromCalibration: "Loss from calibration",
    totalLoss: "Total loss",
    distributionBySize: "Distribution By Size",
    qualityDistribution: "Quality Distribution by Origin",
    warehouseStockOverview: "Warehouse Stock Overview",
    filter: "Filter",
    totalWarehouseStock: "Total Warehouse Stock",
    totalZones: "Total Zones",
    totalOrigins: "Total Origins",
    avgPerZone: "Avg. per Zone",
    zone: "Zone",
    origin: "Origin",
    total: "Total",
    size: "Size",
    veryBad: "Very Bad",
    bad: "Bad",
    regular: "Regular",
    good: "Good",
    veryGood: "Very Good",
    loading: "Loading",
    pleaseWait: "Please wait...",
    dataLoadingError: "Data Loading Error",
    retry: "Retry",
    noWarehouseData: "No Warehouse Data Available",
    noDataMessage: "There is currently no data to display. This could be because the warehouse is empty or data has not been synchronized yet.",
    highMoistureLimit: "High Moisture Limit",
    lowMoistureLimit: "Low Moisture Limit",
    averageActualMoisture: "Average Actual Moisture",
    averageTotalMoistureLoss: "Average Total Moisture Loss"
  },
  pt: {
    totalStock: "Estoque Total",
    averageMoisture: "Umidade Média de Recebimento",
    mostPrevalentSize: "Tamanho Mais Prevalente",
    premiumQualityRegion: "Região de Qualidade Premium",
    moistureLevels: "Níveis Médios de Umidade nas Etapas de Processamento",
    moistureContentAnalysis: "Análise de Conteúdo de Umidade",
    moistureLossProcess: "Fluxo de Perda de Umidade",
    receivingStage: "Etapa de Recebimento",
    calibrationStage: "Etapa de Calibração",
    consumptionStage: "Etapa de Consumo",
    initialMoisture: "Umidade inicial",
    calibratedMoisture: "Umidade calibrada",
    finalMoisture: "Umidade final",
    lossFromAnalysis: "Perda da análise",
    gainFromCalibration: "Ganho da calibração",
    lossFromCalibration: "Perda da calibração",
    totalLoss: "Perda total",
    distributionBySize: "Distribuição por Tamanho",
    qualityDistribution: "Distribuição de Qualidade por Origem",
    warehouseStockOverview: "Visão Geral do Estoque do Armazém",
    filter: "Filtrar",
    totalWarehouseStock: "Estoque Total do Armazém",
    totalZones: "Total de Zonas",
    totalOrigins: "Total de Origens",
    avgPerZone: "Média por Zona",
    zone: "Zona",
    origin: "Origem",
    total: "Total",
    size: "Tamanho",
    veryBad: "Muito Ruim",
    bad: "Ruim",
    regular: "Regular",
    good: "Bom",
    veryGood: "Muito Bom",
    loading: "Carregando",
    pleaseWait: "Por favor, aguarde...",
    dataLoadingError: "Erro ao Carregar Dados",
    retry: "Tentar Novamente",
    noWarehouseData: "Sem Dados do Armazém",
    noDataMessage: "Não há dados para exibir no momento. Isso pode ser porque o armazém está vazio ou os dados ainda não foram sincronizados.",
    highMoistureLimit: "Limite de Umidade Alto",
    lowMoistureLimit: "Limite de Umidade Baixo",
    averageActualMoisture: "Umidade Média Real",
    averageTotalMoistureLoss: "Perda Média Total de Umidade"
  }
};

const WarehouseDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    summaryMetrics: {},
    consumptionMetrics: {},
    qualityDistribution: {},
    sizeDistribution: {},
    stockByOrigin: [],
    stockBySize: [],
    warehouseStockData: [],
    moistureLevels: {},
    moistureLoss: {}
  });
  
  const [alertsData, setAlertsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const [expandedZones, setExpandedZones] = useState([]);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [error, setError] = useState(null);
  const [resolvingAlerts, setResolvingAlerts] = useState({});
  const [language, setLanguage] = useState('en');

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate.toISOString().split('T')[0]);
      if (endDate) params.append('endDate', endDate.toISOString().split('T')[0]);
      
      const response = await axios.get(
        `https://usibras-web-backend.onrender.com/api/dashboard?${params.toString()}`
      );
      
      if (response.data.success) {
        setDashboardData(response.data.data);
      } else {
        setError("Failed to load dashboard data: " + (response.data.message || "Unknown error"));
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError("Failed to connect to the server. Please check your connection and try again.");
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      setError(null);
      const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-alerts');
      if (response.data.success) {
        setAlertsData(response.data.data);
      } else {
        console.error('Error in alerts response:', response.data);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchAlerts();
  }, []);

  useEffect(() => {
    if (dashboardData.warehouseStockData) {
      setExpandedZones(Array(dashboardData.warehouseStockData.length).fill(false));
    }
  }, [dashboardData.warehouseStockData]);

  // Custom colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];
  const MOISTURE_COLORS = ['#8884d8', '#82ca9d', '#ffc658'];
  const QUALITY_COLORS = ['#4CAF50', '#8BC34A', '#FFC107', '#FF9800', '#F44336'];

  // Format size distribution data for pie chart
  const formatSizeDistributionData = () => {
    if (!dashboardData.sizeDistribution) return [];
    
    // Check if we have percentages data
    if (dashboardData.sizeDistribution.percentages) {
      return [
        { name: 'Size 15', value: parseFloat(dashboardData.sizeDistribution.percentages.size15 || 0) },
        { name: 'Size 17', value: parseFloat(dashboardData.sizeDistribution.percentages.size17 || 0) },
        { name: 'Size 19', value: parseFloat(dashboardData.sizeDistribution.percentages.size19 || 0) },
        { name: 'Size 21', value: parseFloat(dashboardData.sizeDistribution.percentages.size21 || 0) },
        { name: 'Size 23', value: parseFloat(dashboardData.sizeDistribution.percentages.size23 || 0) },
        { name: 'Size 25', value: parseFloat(dashboardData.sizeDistribution.percentages.size25 || 0) },
        { name: 'Size 28', value: parseFloat(dashboardData.sizeDistribution.percentages.size28 || 0) }
      ];
    }
    
    // Fallback to the old format if percentages aren't available
    return [
      { name: 'Size 15', value: parseFloat(dashboardData.sizeDistribution.size15 || 0) },
      { name: 'Size 17', value: parseFloat(dashboardData.sizeDistribution.size17 || 0) },
      { name: 'Size 19', value: parseFloat(dashboardData.sizeDistribution.size19 || 0) },
      { name: 'Size 21', value: parseFloat(dashboardData.sizeDistribution.size21 || 0) },
      { name: 'Size 23', value: parseFloat(dashboardData.sizeDistribution.size23 || 0) },
      { name: 'Size 25', value: parseFloat(dashboardData.sizeDistribution.size25 || 0) },
      { name: 'Size 28', value: parseFloat(dashboardData.sizeDistribution.size28 || 0) }
    ];
  };

  // Format quality distribution data for horizontal bar chart by origin
  const formatQualityDistributionData = () => {
    if (!dashboardData.qualityDistribution) return [];
    
    // If we're showing by origin, prepare data for horizontal bar chart
    if (dashboardData.qualityDistribution.byOrigin) {
      const origins = Object.keys(dashboardData.qualityDistribution.byOrigin);
      
      // Create an array of data points for each origin
      const data = origins.map(origin => {
        const qualityData = dashboardData.qualityDistribution.byOrigin[origin];
        
        // Calculate total for this origin to determine percentages
        const originTotal = (qualityData.veryBad || 0) + (qualityData.bad || 0) + 
                            (qualityData.regular || 0) + (qualityData.good || 0) + 
                            (qualityData.veryGood || 0);
        
        return {
          name: origin,
          // Convert raw counts to percentages
          veryBad: originTotal > 0 ? Math.round((qualityData.veryBad || 0) / originTotal * 100) : 0,
          bad: originTotal > 0 ? Math.round((qualityData.bad || 0) / originTotal * 100) : 0,
          regular: originTotal > 0 ? Math.round((qualityData.regular || 0) / originTotal * 100) : 0,
          good: originTotal > 0 ? Math.round((qualityData.good || 0) / originTotal * 100) : 0,
          veryGood: originTotal > 0 ? Math.round((qualityData.veryGood || 0) / originTotal * 100) : 0,
          // Store raw values for reference
          rawTotal: originTotal
        };
      });
      
      // Sort by total quality score (descending)
      data.sort((a, b) => b.rawTotal - a.rawTotal);
      
      return data;
    }
    
    // Fallback to total if byOrigin is not available
    const total = dashboardData.qualityDistribution.total || dashboardData.qualityDistribution;
    const totalSum = (total.veryBad || 0) + (total.bad || 0) + 
                     (total.regular || 0) + (total.good || 0) + 
                     (total.veryGood || 0);
    
    return [
      { name: 'Very Bad', value: totalSum > 0 ? Math.round((total.veryBad || 0) / totalSum * 100) : 0 },
      { name: 'Bad', value: totalSum > 0 ? Math.round((total.bad || 0) / totalSum * 100) : 0 },
      { name: 'Regular', value: totalSum > 0 ? Math.round((total.regular || 0) / totalSum * 100) : 0 },
      { name: 'Good', value: totalSum > 0 ? Math.round((total.good || 0) / totalSum * 100) : 0 },
      { name: 'Very Good', value: totalSum > 0 ? Math.round((total.veryGood || 0) / totalSum * 100) : 0 }
    ];
  };

  // Format stock by origin data for bar chart
  const formatStockByOriginData = () => {
    return dashboardData.stockByOrigin || [];
  };

  // Format stock by size data for bar chart
  const formatStockBySizeData = () => {
    if (!dashboardData.stockBySize) return [];
    
    return dashboardData.stockBySize.map(item => ({
      ...item,
      size: item.size.replace('size_', 'Size '),
      totalStock: parseFloat(item.totalStock)
    }));
  };

  // Format moisture metrics data for radar chart
  const formatMoistureMetricsData = () => {
    if (!dashboardData.summaryMetrics || !dashboardData.consumptionMetrics) return [];
    
    return [
      {
        subject: 'Analysis',
        A: parseFloat(dashboardData.summaryMetrics.averageMoisture || 0),
        fullMark: 15
      },
      {
        subject: 'Calibration',
        A: parseFloat(dashboardData.summaryMetrics.averageMoisture || 0) * 0.9, // Estimated calibration moisture
        fullMark: 15
      },
      {
        subject: 'Consumption',
        A: parseFloat(dashboardData.consumptionMetrics.averageMoisture || 0),
        fullMark: 15
      }
    ];
  };

  // Calculate moisture loss between stages
  const calculateMoistureLoss = () => {
    if (!dashboardData.summaryMetrics || !dashboardData.consumptionMetrics) return [];
    
    const analysisMoisture = parseFloat(dashboardData.summaryMetrics.averageMoisture || 0);
    const consumptionMoisture = parseFloat(dashboardData.consumptionMetrics.averageMoisture || 0);
    const calibrationMoisture = analysisMoisture * 0.9; // Estimated calibration moisture
    
    return [
      { stage: "Analysis", moisture: analysisMoisture, loss: 0 },
      { stage: "Calibration", moisture: calibrationMoisture, loss: analysisMoisture - calibrationMoisture },
      { stage: "Consumption", moisture: consumptionMoisture, loss: analysisMoisture - consumptionMoisture }
    ];
  };

  // Custom tooltip for pie charts
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border border-gray-200 shadow-md rounded-md">
          <p className="font-medium">{`${payload[0].name}: ${payload[0].value.toFixed(1)}%`}</p>
        </div>
      );
    }
    return null;
  };

  // Function to determine alert type based on moisture value
  const getAlertType = (moisture) => {
    const moistureValue = parseFloat(moisture);
    if (moistureValue < 5) return "low";
    if (moistureValue > 15) return "high";
    return "normal";
  };

  // Function to mark an alert as resolved
  const markAlertAsResolved = async (alertId) => {
    try {
      // Set loading state for this specific alert
      setResolvingAlerts(prev => ({ ...prev, [alertId]: true }));
      
      // Send PUT request to resolve the alert
      const response = await axios.put('https://usibras-web-backend.onrender.com/api/resolve-alert', {
        id: alertId
      });
      
      if (response.data.success) {
        // Update the local state to reflect the change
        setAlertsData(prevAlerts => 
          prevAlerts.map(alert => 
            alert.id === alertId ? {...alert, status: "Resolved"} : alert
          )
        );
        
        // Show success toast message
        toast.success(response.data.message || `Alert ${alertId} has been resolved successfully`);
      } else {
        // Show error toast if the API returns an error
        toast.error(response.data.message || "Failed to resolve alert");
      }
    } catch (error) {
      console.error('Error resolving alert:', error);
      toast.error("Failed to connect to the server. Please try again.");
    } finally {
      // Clear loading state for this alert
      setResolvingAlerts(prev => {
        const newState = { ...prev };
        delete newState[alertId];
        return newState;
      });
    }
  };

  const SkeletonLoader = () => {
    return (
      <div className="animate-pulse">
        {/* Alerts Section Skeleton */}
        <div className="mb-5">
          <div className="h-7 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between">
                  <div className="h-6 bg-gray-200 rounded w-32"></div>
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                </div>
                <div className="mt-3 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
                <div className="mt-3 pt-2 border-t border-gray-100">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
            <div className="h-[300px] bg-gray-100 rounded-lg"></div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
            <div className="h-[300px] bg-gray-100 rounded-lg"></div>
          </div>
        </div>

        {/* Warehouse Stock Skeleton */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="h-6 bg-gray-200 rounded w-56 mb-4"></div>
          <div className="bg-gray-100 p-3 rounded-lg mb-4">
            <div className="h-5 bg-gray-200 rounded w-full mb-3"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-gray-200 rounded-lg">
                <div className="bg-gray-100 p-3 flex justify-between items-center">
                  <div className="h-5 bg-gray-200 rounded w-24"></div>
                  <div className="h-5 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Add an error display component
  const ErrorMessage = ({ message, onRetry }) => (
    <Card extra="p-6">
      <div className="text-center">
        <MdBugReport className="mx-auto text-red-500" size={48} />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Data Loading Error</h3>
        <p className="mt-2 text-sm text-gray-500">{message}</p>
        <div className="mt-6">
          <button
            onClick={onRetry}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <MdRefresh className="-ml-1 mr-2 h-5 w-5" />
            Retry
          </button>
        </div>
      </div>
    </Card>
  );

  // Add a component for empty data state
  const EmptyDataMessage = () => (
    <Card extra="p-6">
      <div className="text-center">
        <MdOutlineWarehouse className="mx-auto text-gray-400" size={48} />
        <h3 className="mt-4 text-lg font-medium text-gray-900">No Warehouse Data Available</h3>
        <p className="mt-2 text-sm text-gray-500">
          There is currently no data to display. This could be because the warehouse is empty or data has not been synchronized yet.
        </p>
      </div>
    </Card>
  );

  // Create a function to format the moisture data for charts
  const formatMoistureData = () => {
    if (!dashboardData.moistureLevels || !dashboardData.moistureLoss) {
      return [];
    }
    
    return [
      { 
        name: 'Receiving', 
        moisture: parseFloat(dashboardData.moistureLevels.analysisMoisture || 0),
        loss: 0
      },
      { 
        name: 'Calibration', 
        moisture: parseFloat(dashboardData.moistureLevels.calibratedMoisture || 0),
        loss: parseFloat(dashboardData.moistureLoss.analysisToCalibration || 0)
      },
      { 
        name: 'Consumption', 
        moisture: parseFloat(dashboardData.moistureLevels.consumptionMoisture || 0),
        loss: parseFloat(dashboardData.moistureLoss.calibrationToConsumption || 0)
      }
    ];
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'pt' : 'en');
  };

  return (
    <div className="mt-5 grid grid-cols-1 gap-5">
      {/* Language Toggle Button */}
      <button
        onClick={toggleLanguage}
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center rounded-full bg-brand-500 p-3 text-white shadow-lg hover:bg-brand-600 transition-all duration-200"
        aria-label="Toggle language"
      >
        <MdTranslate className="h-6 w-6" />
        <span className="ml-2 text-sm font-medium">{language.toUpperCase()}</span>
      </button>

      {loading ? (
        <SkeletonLoader />
      ) : error ? (
        <ErrorMessage 
          message={error} 
          onRetry={() => {
            fetchDashboardData();
            fetchAlerts();
          }} 
        />
      ) : !dashboardData || Object.keys(dashboardData).length === 0 ? (
        <EmptyDataMessage />
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
            {/* Total Stock Card */}
            <Card extra="p-4">
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-full mr-4">
                  <MdOutlineWarehouse className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{translations[language].totalStock}</p>
                  <p className="text-xl font-bold">
                    {(dashboardData.warehouseStockData || []).reduce((total, zone) => 
                      total + zone.origins.reduce((zoneTotal, origin) => 
                        zoneTotal + origin.sizes.reduce((originTotal, size) => 
                          originTotal + parseFloat(size.kg || 0), 0), 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} KG
                  </p>
                </div>
              </div>
            </Card>

            {/* Average Moisture Card */}
            <Card extra="p-4">
              <div className="flex items-center">
                <div className="bg-green-100 p-3 rounded-full mr-4">
                  <MdWaterDrop className="text-green-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{translations[language].averageMoisture}</p>
                  <p className="text-xl font-bold">
                    {dashboardData.moistureLevels?.analysisMoisture ? 
                      `${parseFloat(dashboardData.moistureLevels.analysisMoisture).toFixed(1)}%` : 
                      'N/A'}
                  </p>
                </div>
              </div>
            </Card>

            {/* Most Dominant Size Card */}
            <Card extra="p-4">
              <div className="flex items-center">
                <div className="bg-purple-100 p-3 rounded-full mr-4">
                  <MdBarChart className="text-purple-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{translations[language].mostPrevalentSize}</p>
                  <p className="text-xl font-bold">
                    {(() => {
                      const sizeTotals = {};
                      (dashboardData.warehouseStockData || []).forEach(zone => {
                        zone.origins.forEach(origin => {
                          origin.sizes.forEach(size => {
                            const sizeName = size.size.replace('size_', 'Size ');
                            sizeTotals[sizeName] = (sizeTotals[sizeName] || 0) + parseFloat(size.kg || 0);
                          });
                        });
                      });
                      
                      const dominantSize = Object.entries(sizeTotals)
                        .sort(([,a], [,b]) => b - a)[0];
                      
                      return dominantSize ? dominantSize[0] : 'N/A';
                    })()}
                  </p>
                </div>
              </div>
            </Card>

            {/* Best Quality Region Card */}
            <Card extra="p-4">
              <div className="flex items-center">
                <div className="bg-orange-100 p-3 rounded-full mr-4">
                  <MdAssessment className="text-orange-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{translations[language].premiumQualityRegion}</p>
                  <p className="text-xl font-bold">
                    {(() => {
                      if (!dashboardData.qualityDistribution?.byOrigin) return 'N/A';
                      
                      const origins = Object.entries(dashboardData.qualityDistribution.byOrigin);
                      if (origins.length === 0) return 'N/A';
                      
                      // Calculate quality score for each origin (weighted average)
                      const originScores = origins.map(([origin, data]) => {
                        const total = (data.veryBad || 0) + (data.bad || 0) + 
                                    (data.regular || 0) + (data.good || 0) + 
                                    (data.veryGood || 0);
                        
                        if (total === 0) return [origin, 0];
                        
                        // Weight the quality levels (1-5 scale)
                        const score = (
                          (data.veryBad || 0) * 1 +
                          (data.bad || 0) * 2 +
                          (data.regular || 0) * 3 +
                          (data.good || 0) * 4 +
                          (data.veryGood || 0) * 5
                        ) / total;
                        
                        return [origin, score];
                      });
                      
                      // Sort by score and get the highest
                      const bestOrigin = originScores.sort(([,a], [,b]) => b - a)[0];
                      return bestOrigin ? bestOrigin[0] : 'N/A';
                    })()}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Moisture Metrics */}
          {dashboardData.moistureLevels && dashboardData.moistureLoss && (
            <Card extra="p-4 overflow-hidden">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold flex items-center">
                  <MdWaterDrop className="text-blue-500 mr-2" size={24} />
                  {translations[language].moistureLevels}
                </h3>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Moisture Levels Chart - Enhanced UI */}
                <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl p-5 shadow-sm">
                  <h4 className="text-md font-medium mb-4 text-gray-700">{translations[language].moistureContentAnalysis}</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={formatMoistureData()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fill: '#6b7280' }} />
                      <YAxis 
                        label={{ value: 'Moisture Content (%)', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 12 }} 
                        tick={{ fill: '#6b7280' }}
                      />
                      <Tooltip 
                        formatter={(value) => [`${value}%`, 'Moisture']}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
                      />
                      <Legend iconType="circle" />
                      <ReferenceLine y={15} stroke="#ef4444" strokeDasharray="3 3" strokeWidth={2}>
                        <Label position="right" value={translations[language].highMoistureLimit} fill="#ef4444" fontSize={11} />
                      </ReferenceLine>
                      <ReferenceLine y={4} stroke="#3b82f6" strokeDasharray="3 3" strokeWidth={2}>
                        <Label position="right" value={translations[language].lowMoistureLimit} fill="#3b82f6" fontSize={11} />
                      </ReferenceLine>
                      <Bar 
                        dataKey="moisture" 
                        name={translations[language].averageActualMoisture} 
                        fill="url(#moistureGradient)" 
                        radius={[4, 4, 0, 0]}
                        barSize={50}
                      >
                        <LabelList 
                          dataKey="moisture" 
                          position="top" 
                          formatter={(value) => `${value}%`} 
                          fill="#4b5563" 
                          fontSize={12} 
                          fontWeight="bold"
                        />
                      </Bar>
                      <defs>
                        <linearGradient id="moistureGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#6366f1" stopOpacity={0.8}/>
                          <stop offset="100%" stopColor="#818cf8" stopOpacity={0.6}/>
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Moisture Loss Process Flow - Adjusted positioning for loss/gain labels */}
                <div className="bg-gradient-to-br from-white to-purple-50 rounded-xl p-5 shadow-sm">
                  <h4 className="text-md font-medium mb-4 text-gray-700">{translations[language].moistureLossProcess}</h4>
                  <div className="relative h-[350px] w-full">
                    {/* Process Flow Diagram */}
                    <div className="absolute top-[150px] left-0 right-0 flex items-center justify-between px-8">
                      <div className="h-3 bg-gradient-to-r from-blue-200 via-purple-200 to-green-200 w-full rounded-full relative">
                        {/* Analysis to Calibration Loss - POSITIONED IN THE MIDDLE */}
                        <div className="absolute -top-[70px] left-[25%] transform -translate-x-1/2">
                          <div className="bg-blue-600 text-white text-sm font-bold px-4 py-2 rounded-lg inline-block shadow-md border border-blue-700">
                            {translations[language].lossFromAnalysis}: {dashboardData.moistureLoss.analysisToCalibration}%
                          </div>
                          <div className="h-[50px] w-1 bg-gradient-to-b from-blue-600 to-purple-600 mx-auto mt-1"></div>
                        </div>
                        
                        {/* Calibration to Consumption Loss/Gain - FULLY DYNAMIC */}
                        <div className="absolute -top-[70px] left-[75%] transform -translate-x-1/2">
                          <div className={`${
                            parseFloat(dashboardData.moistureLoss.calibrationToConsumption) < 0 
                              ? 'bg-green-600 text-white border-green-700' 
                              : 'bg-blue-600 text-white border-blue-700'
                          } text-sm font-bold px-4 py-2 rounded-lg inline-block shadow-md border`}>
                            {parseFloat(dashboardData.moistureLoss.calibrationToConsumption) < 0 
                              ? `Gain: ${Math.abs(parseFloat(dashboardData.moistureLoss.calibrationToConsumption))}%` 
                              : `Loss: ${parseFloat(dashboardData.moistureLoss.calibrationToConsumption)}%`
                            }
                          </div>
                          <div className={`h-[50px] w-1 ${
                            parseFloat(dashboardData.moistureLoss.calibrationToConsumption) < 0 
                              ? 'bg-gradient-to-b from-purple-600 to-green-600' 
                              : 'bg-gradient-to-b from-purple-600 to-blue-600'
                          } mx-auto mt-1`}></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Stage Boxes - With exact positioning */}
                    <div className="absolute top-[150px] left-0 right-0">
                      <div className="flex justify-between px-4">
                        {/* Analysis Stage - Positioned at 0% */}
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-4 shadow-lg z-10 w-28 transform transition-transform hover:scale-105">
                          <div className="text-sm font-medium">{translations[language].receivingStage}</div>
                          <div className="text-xl font-bold">{dashboardData.moistureLevels.analysisMoisture}%</div>
                        </div>
                        
                        {/* Calibration Stage - Positioned at 50% */}
                        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg p-4 shadow-lg z-10 w-28 transform transition-transform hover:scale-105">
                          <div className="text-sm font-medium">{translations[language].calibrationStage}</div>
                          <div className="text-xl font-bold">~{dashboardData.moistureLevels.calibratedMoisture}%</div>
                        </div>
                        
                        {/* Consumption Stage - Positioned at 100% */}
                        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-4 shadow-lg z-10 w-28 transform transition-transform hover:scale-105">
                          <div className="text-sm font-medium">{translations[language].consumptionStage}</div>
                          <div className="text-xl font-bold">{dashboardData.moistureLevels.consumptionMoisture}%</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Total Loss */}
                    <div className="absolute bottom-4 left-0 right-0 text-center">
                      <div className="bg-gray-800 text-white text-sm font-medium px-4 py-2 rounded-lg inline-block shadow-md">
                        {translations[language].totalLoss}: <span className="font-bold text-yellow-300">{dashboardData.moistureLoss.totalLoss}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Stage Details - Enhanced UI */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 shadow-sm border border-blue-200 transform transition-all hover:shadow-md">
                  <div className="flex items-center mb-3">
                    <div className="bg-blue-500 p-2 rounded-full mr-3">
                      <MdWaterDrop className="text-white" size={18} />
                    </div>
                    <h4 className="font-medium text-blue-800">{translations[language].receivingStage}</h4>
                  </div>
                  <p className="text-sm text-gray-700">{translations[language].initialMoisture}: <span className="font-bold text-blue-700">{dashboardData.moistureLevels.analysisMoisture}%</span></p>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 shadow-sm border border-purple-200 transform transition-all hover:shadow-md">
                  <div className="flex items-center mb-3">
                    <div className="bg-purple-500 p-2 rounded-full mr-3">
                      <MdBalance className="text-white" size={18} />
                    </div>
                    <h4 className="font-medium text-purple-800">{translations[language].calibrationStage}</h4>
                  </div>
                  <p className="text-sm text-gray-700">{translations[language].calibratedMoisture}: <span className="font-bold text-purple-700">~{dashboardData.moistureLevels.calibratedMoisture}%</span></p>
                  <p className="text-sm text-gray-700 mt-2">{translations[language].lossFromAnalysis}: <span className="font-bold text-purple-700">{dashboardData.moistureLoss.analysisToCalibration}%</span></p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 shadow-sm border border-green-200 transform transition-all hover:shadow-md">
                  <div className="flex items-center mb-3">
                    <div className="bg-green-500 p-2 rounded-full mr-3">
                      <MdFactory className="text-white" size={18} />
                    </div>
                    <h4 className="font-medium text-green-800">{translations[language].consumptionStage}</h4>
                  </div>
                  <p className="text-sm text-gray-700">{translations[language].finalMoisture}: <span className="font-bold text-green-700">{dashboardData.moistureLevels.consumptionMoisture}%</span></p>
                  <p className="text-sm text-gray-700 mt-2">
                    {parseFloat(dashboardData.moistureLoss.calibrationToConsumption) < 0 ? (
                      <>{translations[language].gainFromCalibration}: <span className="font-bold text-green-700">{Math.abs(parseFloat(dashboardData.moistureLoss.calibrationToConsumption))}%</span></>
                    ) : (
                      <>{translations[language].lossFromCalibration}: <span className="font-bold text-green-700">{dashboardData.moistureLoss.calibrationToConsumption}%</span></>
                    )}
                  </p>
                  <p className="text-sm text-gray-700 mt-2">{translations[language].totalLoss}: <span className="font-bold text-green-700">{dashboardData.moistureLoss.totalLoss}%</span></p>
                </div>
              </div>
            </Card>
          )}

          {/* Size and Quality Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card extra="p-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold flex items-center">
                  <MdBarChart className="text-indigo-500 mr-2" size={24} />
                  {translations[language].distributionBySize}
                </h3>
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer>
                  <BarChart
                    data={formatSizeDistributionData()}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis 
                      label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Percentage']} />
                    <Legend />
                    <Bar dataKey="value" name="Size Distribution">
                      {/* Custom colors for each size */}
                      <Cell key="size15" fill="#FFD700" /> {/* Size 15 - Gold/Yellow */}
                      <Cell key="size17" fill="#FF69B4" /> {/* Size 17 - Pink */}
                      <Cell key="size19" fill="#800080" /> {/* Size 19 - Purple */}
                      <Cell key="size21" fill="#00BFFF" /> {/* Size 21 - Light Blue */}
                      <Cell key="size23" fill="#FFFF00" /> {/* Size 23 - Yellow */}
                      <Cell key="size25" fill="#9370DB" /> {/* Size 25 - Medium Purple */}
                      <Cell key="size28" fill="#D3D3D3" /> {/* Size 28 - Light Gray */}
                      <LabelList dataKey="value" position="top" formatter={(value) => `${value.toFixed(1)}%`} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-1 rounded-full" style={{ backgroundColor: '#FFD700' }}></div>
                  <span className="text-sm">Size 15</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-1 rounded-full" style={{ backgroundColor: '#FF69B4' }}></div>
                  <span className="text-sm">Size 17</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-1 rounded-full" style={{ backgroundColor: '#800080' }}></div>
                  <span className="text-sm">Size 19</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-1 rounded-full" style={{ backgroundColor: '#00BFFF' }}></div>
                  <span className="text-sm">Size 21</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-1 rounded-full" style={{ backgroundColor: '#FFFF00' }}></div>
                  <span className="text-sm">Size 23</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-1 rounded-full" style={{ backgroundColor: '#9370DB' }}></div>
                  <span className="text-sm">Size 25</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-1 rounded-full" style={{ backgroundColor: '#D3D3D3' }}></div>
                  <span className="text-sm">Size 28</span>
                </div>
              </div>
            </Card>

            <Card extra="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <MdAssessment className="text-teal-500 mr-2" size={24} />
                  {translations[language].qualityDistribution}
                </h3>
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer>
                  {dashboardData.qualityDistribution?.byOrigin ? (
                    <BarChart
                      layout="vertical"
                      data={formatQualityDistributionData()}
                      margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" label={{ value: 'Percentage (%)', position: 'insideBottom', offset: -5 }} />
                      <YAxis type="category" dataKey="name" width={80} />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Legend />
                      <Bar dataKey="veryBad" name="Very Bad" stackId="a" fill="#FF5252" />
                      <Bar dataKey="bad" name="Bad" stackId="a" fill="#FF9800" />
                      <Bar dataKey="regular" name="Regular" stackId="a" fill="#FFC107" />
                      <Bar dataKey="good" name="Good" stackId="a" fill="#4CAF50" />
                      <Bar dataKey="veryGood" name="Very Good" stackId="a" fill="#2196F3" />
                    </BarChart>
                  ) : (
                    <PieChart>
                      <Pie
                        data={formatQualityDistributionData()}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {formatQualityDistributionData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={QUALITY_COLORS[index % QUALITY_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                    </PieChart>
                  )}
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Warehouse Stock Overview - With Total Weight */}
          <Card extra="p-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{translations[language].warehouseStockOverview}</h3>
              <div className="flex items-center">
                <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <MdFilterAlt className="mr-1" /> {translations[language].filter}
                </button>
              </div>
            </div>
            
            {/* Total Weight Summary */}
            <div className="bg-blue-50 p-3 rounded-lg mb-4">
              <div className="flex flex-wrap items-center justify-between">
                <div className="flex items-center">
                  <MdInventory className="text-blue-600 mr-2" size={24} />
                  <span className="font-medium text-blue-800">{translations[language].totalWarehouseStock}</span>
                </div>
                <div className="text-xl font-bold text-blue-700">
                  {(dashboardData.warehouseStockData || []).reduce((total, zone) => 
                    total + zone.origins.reduce((zoneTotal, origin) => 
                      zoneTotal + origin.sizes.reduce((originTotal, size) => 
                        originTotal + parseFloat(size.kg || 0), 0), 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} KG
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <div>
                  <div className="text-xs text-gray-500">{translations[language].totalZones}</div>
                  <div className="text-lg font-semibold">{(dashboardData.warehouseStockData || []).length}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">{translations[language].totalOrigins}</div>
                  <div className="text-lg font-semibold">
                    {(dashboardData.warehouseStockData || []).reduce((total, zone) => 
                      total + zone.origins.length, 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">{translations[language].avgPerZone}</div>
                  <div className="text-lg font-semibold">
                    {((dashboardData.warehouseStockData || []).reduce((total, zone) => 
                      total + zone.origins.reduce((zoneTotal, origin) => 
                        zoneTotal + origin.sizes.reduce((originTotal, size) => 
                          originTotal + parseFloat(size.kg || 0), 0), 0), 0) / 
                      Math.max(1, (dashboardData.warehouseStockData || []).length)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} KG
                  </div>
                </div>
              </div>
            </div>
            
            {/* Collapsible Zone Sections */}
            <div className="space-y-3">
              {(dashboardData.warehouseStockData || []).map((zone, zoneIndex) => (
                <div key={zoneIndex} className="border rounded-lg overflow-hidden">
                  <div 
                    className="bg-gray-50 p-3 flex justify-between items-center cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setExpandedZones(prevState => {
                        const newState = [...prevState];
                        newState[zoneIndex] = !newState[zoneIndex];
                        return newState;
                      });
                    }}
                  >
                    <div className="flex items-center">
                      <MdOutlineWarehouse className="text-gray-600 mr-2" size={20} />
                      <span className="font-medium">Zone {zone.zone}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-3">
                        {zone.origins.reduce((total, origin) => 
                          total + origin.sizes.reduce((sum, size) => sum + parseFloat(size.kg || 0), 0), 0).toFixed(2)} KG
                      </span>
                      <MdExpandMore className={`transform transition-transform ${expandedZones[zoneIndex] ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  
                  {expandedZones[zoneIndex] && (
                    <div className="p-3">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Origin</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size 17</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size 19</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size 21</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size 23</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size 25</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size 28</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {zone.origins.map((origin, originIndex) => (
                              <tr key={originIndex} className="hover:bg-gray-50">
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{origin.origin}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                                  {parseFloat(origin.sizes.find(s => s.size === 'size_17')?.kg || 0) > 0 ? 
                                    parseFloat(origin.sizes.find(s => s.size === 'size_17')?.kg || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                                  {parseFloat(origin.sizes.find(s => s.size === 'size_19')?.kg || 0) > 0 ? 
                                    parseFloat(origin.sizes.find(s => s.size === 'size_19')?.kg || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                                  {parseFloat(origin.sizes.find(s => s.size === 'size_21')?.kg || 0) > 0 ? 
                                    parseFloat(origin.sizes.find(s => s.size === 'size_21')?.kg || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                                  {parseFloat(origin.sizes.find(s => s.size === 'size_23')?.kg || 0) > 0 ? 
                                    parseFloat(origin.sizes.find(s => s.size === 'size_23')?.kg || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                                  {parseFloat(origin.sizes.find(s => s.size === 'size_25')?.kg || 0) > 0 ? 
                                    parseFloat(origin.sizes.find(s => s.size === 'size_25')?.kg || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                                  {parseFloat(origin.sizes.find(s => s.size === 'size_28')?.kg || 0) > 0 ? 
                                    parseFloat(origin.sizes.find(s => s.size === 'size_28')?.kg || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                  {origin.sizes.reduce((sum, size) => sum + parseFloat(size.kg || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50">
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">Total</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                {zone.origins.reduce((sum, origin) => 
                                  sum + parseFloat(origin.sizes.find(s => s.size === 'size_17')?.kg || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                {zone.origins.reduce((sum, origin) => 
                                  sum + parseFloat(origin.sizes.find(s => s.size === 'size_19')?.kg || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                {zone.origins.reduce((sum, origin) => 
                                  sum + parseFloat(origin.sizes.find(s => s.size === 'size_21')?.kg || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                {zone.origins.reduce((sum, origin) => 
                                  sum + parseFloat(origin.sizes.find(s => s.size === 'size_23')?.kg || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                {zone.origins.reduce((sum, origin) => 
                                  sum + parseFloat(origin.sizes.find(s => s.size === 'size_25')?.kg || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                {zone.origins.reduce((sum, origin) => 
                                  sum + parseFloat(origin.sizes.find(s => s.size === 'size_28')?.kg || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                {zone.origins.reduce((total, origin) => 
                                  total + origin.sizes.reduce((sum, size) => sum + parseFloat(size.kg || 0), 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default WarehouseDashboard; 